import { createSelector } from '@reduxjs/toolkit';
import questionFunctions from '../utils/functions/question';
import _ from 'lodash';
import codes from '../utils/constants/codes';
import moment from 'moment';

const claimReviewProjections = {};

claimReviewProjections.claimReviewOverview = createSelector(
    state => state.claimReview.claimReviewDetails.overview,
    claimReviewDetailsOverview => {
        if (!claimReviewDetailsOverview) {
            return null;
        }

        return {
            formattedType:
                `${claimReviewDetailsOverview.policyTypeName || ''} - 
                ${claimReviewDetailsOverview.claimCategoryName ? claimReviewDetailsOverview.claimCategoryName + ' - ' : ''}
                ${claimReviewDetailsOverview.formTypeName || ''}`.replace(
                    /\s+-\s+$/,
                    '',
                ),
            claimReference: claimReviewDetailsOverview.claimReference,
            createdDate: moment(claimReviewDetailsOverview.CreatedDate).format(
                'DD/MM/YYYY',
            ),
        };
    },
);

claimReviewProjections.acceptedRejectedWordings = createSelector(
    [
        state => state.claimReview.claimReviewAcceptedRejectedWordings,
        state => state.applicationUser.unAuthorisedDetails.languageId,
    ],
    (acceptedClaimReview, languageId) => {
        if (!acceptedClaimReview || !languageId) {
            return null;
        }
        const { byId, allIds } = acceptedClaimReview;
        return byId?.[languageId] || (allIds?.length ? byId[allIds[0]] : null);
    },
);

claimReviewProjections.claimReviewAnswersGroupedByStep = () =>
    createSelector(
        state => state.claimReview.claimReviewDetails,
        claimReviewDetails => {
            if (
                Object.keys(claimReviewDetails.question.byId).length === 0 ||
                claimReviewDetails.question.allIds.length === 0 ||
                Object.keys(claimReviewDetails.questionType.byId).length ===
                    0 ||
                claimReviewDetails.questionType.allIds.length === 0 ||
                Object.keys(claimReviewDetails.questionGroupStep.byId)
                    .length === 0 ||
                claimReviewDetails.questionGroupStep.allIds.length === 0 ||
                claimReviewDetails.answers.length === 0 ||
                claimReviewDetails.currencies.length === 0
            ) {
                return null;
            }

            // recursive function
            const getAnswersOfQuestionTree = (
                question,
                questionsById,
                answers,
                questionTypesById,
                currencyOptions,
            ) => {
                let items = [];
                const answerOfQuestion = answers
                    ? answers.find(a => a.questionCode === question.code)
                    : null;
                const questionType = questionTypesById[question.questionTypeId];
                items.push(
                    questionFunctions.parseQuestionWithAnswer(
                        question,
                        answerOfQuestion,
                        questionType,
                        currencyOptions,
                    ),
                );

                // if option is selected form a dropdown then we have to get all
                //sub question answers
                if (
                    questionType &&
                    questionType.code === 'list' &&
                    answerOfQuestion
                ) {
                    const subQs = Object.values(questionsById).filter(
                        q =>
                            q.enabledByOptionId === answerOfQuestion.optionId &&
                            q.id === question.enabledByQuestionId,
                    );
                    if (subQs) {
                        subQs.forEach(sq => {
                            items = [
                                ...items,
                                ...getAnswersOfQuestionTree(
                                    sq,
                                    claimReviewDetails.question.byId,
                                    claimReviewDetails.answers,
                                    claimReviewDetails.questionType.byId,
                                    claimReviewDetails.currencies,
                                ),
                            ];
                        });
                    }
                }

                return items;
            };

            try {
                // construct the answers
                const answersGroupedByStep = [];
                Object.values(claimReviewDetails.question.byId).forEach(
                    question => {
                        answersGroupedByStep.push(
                            ...getAnswersOfQuestionTree(
                                question,
                                claimReviewDetails.question.byId,
                                claimReviewDetails.answers,
                                claimReviewDetails.questionType.byId,
                                claimReviewDetails.currencies,
                            ),
                        );
                    },
                );

                // sort the question - answer objects
                const sortedAnswersGroupedByStep = _.sortBy(
                    answersGroupedByStep,
                    function (item) {
                        return claimReviewDetails.question.allIds.indexOf(
                            item.question.code,
                        );
                    },
                );

                // group by step and exclude the attachments step
                return _.chain(sortedAnswersGroupedByStep)
                    .groupBy(s => s.question.questionGroupStepId)
                    .map((s, stepId) => {
                        const step = Object.values(
                            claimReviewDetails.questionGroupStep.byId,
                        ).find(i => i.id === stepId);

                        return {
                            ...step,
                            questionsWithAnswers: s,
                        };
                    })
                    .filter(s => s.code !== codes.attachments)
                    .sortBy(s => s.rank)
                    .value();
            } catch (e) {
                return null;
            }
        },
    );

export default claimReviewProjections;
